/**
 * Resolves the subset of Mapbox style-spec expressions this library's
 * colour-by editor actually emits (see getTileColorExpression/
 * colorExpressionToIconImageExpression in useLayers.ts/colorExpressions.ts)
 * against a feature's attributes — i.e. the exact logic Esri/Mapbox use to
 * pick a colour per feature, so test hooks can ask "what colour did THIS
 * asset render as", not just "what's this layer's category colour".
 * Unsupported expression shapes are returned as-is.
 */
export function resolveExpression(
	expr: unknown,
	properties: Record<string, unknown>,
): unknown {
	if (!Array.isArray(expr)) return expr;
	const [head, ...rest] = expr;
	switch (head) {
		case 'get':
			return properties[rest[0] as string];
		case 'to-number': {
			const value = resolveExpression(rest[0], properties);
			const num = Number(value);
			return Number.isNaN(num) ? rest[1] : num;
		}
		case 'concat':
			return rest.map(part => resolveExpression(part, properties)).join('');
		case 'match': {
			// ['match', input, label, out, label, out, ..., fallback]
			const input = resolveExpression(rest[0], properties);
			for (let i = 1; i < rest.length - 1; i += 2) {
				const label = rest[i];
				const labels = Array.isArray(label) ? label : [label];
				if (labels.some(l => String(l) === String(input))) {
					return resolveExpression(rest[i + 1], properties);
				}
			}
			return resolveExpression(rest[rest.length - 1], properties);
		}
		case 'step': {
			// ['step', input, base, stop1, out1, stop2, out2, ...]
			const input = Number(resolveExpression(rest[0], properties));
			let result = resolveExpression(rest[1], properties);
			for (let i = 2; i < rest.length - 1; i += 2) {
				if (input >= Number(rest[i])) {
					result = resolveExpression(rest[i + 1], properties);
				}
			}
			return result;
		}
		default:
			return expr;
	}
}

/** Extracts the trailing `~<color>` (and strips `-selected`/`-nobackground` suffixes) from a resolved icon sprite id, e.g. `"inno:pipe~007ca0-nobackground"` → `"007ca0"`. */
export function colorFromSpriteId(spriteId: unknown): string | undefined {
	if (typeof spriteId !== 'string' || !spriteId.includes('~')) return undefined;
	return spriteId
		.split('~')
		.pop()
		?.replace(/-selected$/, '')
		.replace(/-nobackground$/, '');
}
